import { useState, useEffect, useCallback, useRef } from "react";
import { Package, MapPin, Phone, Navigation, Check, RefreshCw, Locate, LocateOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DriverLayout } from "@/components/layouts/DriverLayout";
import { toast } from "sonner";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

import API from "@/utils/api";

export default function DriverOrders() {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("available");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTrackingLocation, setIsTrackingLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const watchIdRef = useRef(null);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/driver/orders`, { withCredentials: true });
      // Filter only delivery orders (exclude local orders)
      const deliveryOrders = response.data.filter(o => o.order_type !== "local");
      setOrders(deliveryOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Send location to server
  const sendLocation = useCallback(async (lat, lng) => {
    try {
      await axios.post(
        `${API}/driver/location`,
        { lat, lng },
        { withCredentials: true }
      );
      setCurrentLocation({ lat, lng });
    } catch (error) {
      console.error("Error sending location:", error);
    }
  }, []);

  // Start location tracking
  const startLocationTracking = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }

    setIsTrackingLocation(true);
    toast.success("Compartiendo ubicación con los clientes");

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        sendLocation(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Error al obtener ubicación");
      },
      { enableHighAccuracy: true }
    );

    // Watch position changes
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        sendLocation(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.error("Watch position error:", error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 10000,
        timeout: 10000
      }
    );
  }, [sendLocation]);

  // Stop location tracking
  const stopLocationTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTrackingLocation(false);
    setCurrentLocation(null);
    toast.info("Dejaste de compartir tu ubicación");
  }, []);

  // Auto-start location tracking when there are active orders
  useEffect(() => {
    const activeOrders = orders.filter(o => o.status === "on_the_way");
    if (activeOrders.length > 0 && !isTrackingLocation) {
      startLocationTracking();
    }
  }, [orders, isTrackingLocation, startLocationTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, []);

  const handlePickup = async (orderId) => {
    try {
      await axios.post(`${API}/driver/orders/${orderId}/pickup`, {}, { withCredentials: true });
      toast.success("Pedido recogido");
      fetchOrders();
    } catch (error) {
      toast.error("Error al recoger pedido");
    }
  };

  const handleDeliver = async (orderId) => {
    try {
      await axios.post(`${API}/driver/orders/${orderId}/deliver`, {}, { withCredentials: true });
      toast.success("Pedido entregado");
      fetchOrders();
    } catch (error) {
      toast.error("Error al marcar como entregado");
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchOrders();
  };

  const openGoogleMaps = (address) => {
    if (!address) {
      toast.error("Dirección no disponible");
      return;
    }
    const query = encodeURIComponent(`${address.street}, ${address.postal_code} ${address.city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
  };

  const availableOrders = orders.filter((o) => o.status === "ready");
  const activeOrders = orders.filter((o) => o.status === "on_the_way");

  // Default coordinates (Madrid center if no location)
  const mapCenter = currentLocation ? [currentLocation.lat, currentLocation.lng] : [40.4168, -3.7038];

  if (isLoading) {
    return (
      <DriverLayout title="Pedidos">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout title="Pedidos">
      <div className="space-y-6">
        {/* Location Tracking Banner */}
        {activeOrders.length > 0 && (
          <Card className={`border-2 ${isTrackingLocation ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isTrackingLocation ? (
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                      <Locate className="w-5 h-5 text-white" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                      <LocateOff className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">
                      {isTrackingLocation ? "Compartiendo ubicación" : "Ubicación no compartida"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {isTrackingLocation
                        ? "Los clientes pueden ver tu ubicación en tiempo real"
                        : "Activa la ubicación para que los clientes te vean"
                      }
                    </p>
                  </div>
                </div>
                <Button
                  variant={isTrackingLocation ? "outline" : "default"}
                  size="sm"
                  onClick={isTrackingLocation ? stopLocationTracking : startLocationTracking}
                  data-testid="toggle-location-btn"
                >
                  {isTrackingLocation ? (
                    <>
                      <LocateOff className="w-4 h-4 mr-2" />
                      Desactivar
                    </>
                  ) : (
                    <>
                      <Locate className="w-4 h-4 mr-2" />
                      Activar
                    </>
                  )}
                </Button>
              </div>
              {currentLocation && (
                <p className="text-xs text-muted-foreground mt-2">
                  📍 {currentLocation.lat.toFixed(4)}, {currentLocation.lng.toFixed(4)}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Pedidos</h1>
            <p className="text-muted-foreground">
              {availableOrders.length} disponibles • {activeOrders.length} en reparto
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            data-testid="refresh-driver-orders"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="available" data-testid="tab-available">
              Disponibles ({availableOrders.length})
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">
              En reparto ({activeOrders.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="available" className="mt-4 space-y-4">
            {availableOrders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No hay pedidos disponibles</p>
              </div>
            ) : (
              availableOrders.map((order) => (
                <Card key={order.order_id} data-testid={`available-order-${order.order_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">#{order.order_id.slice(-6)}</CardTitle>
                      <Badge className="bg-cyan-100 text-cyan-800">Listo para recoger</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {order.address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">{order.address.street}</p>
                          <p className="text-sm text-muted-foreground">
                            {order.address.postal_code} {order.address.city}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="text-sm">
                      <p className="font-medium mb-1">Productos:</p>
                      {order.items.map((item, i) => (
                        <p key={i} className="text-muted-foreground">
                          {item.quantity}x {item.name}
                        </p>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <p className="font-bold text-lg">{order.total.toFixed(2)} €</p>
                      <div className="flex gap-2">
                        {order.address && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openGoogleMaps(order.address)}
                            data-testid={`navigate-${order.order_id}`}
                          >
                            <Navigation className="w-4 h-4 mr-1" />
                            Navegar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handlePickup(order.order_id)}
                          data-testid={`pickup-${order.order_id}`}
                        >
                          Recoger
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="active" className="mt-4 space-y-4">
            {activeOrders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No tienes pedidos en reparto</p>
              </div>
            ) : (
              activeOrders.map((order) => (
                <Card key={order.order_id} data-testid={`active-order-${order.order_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">#{order.order_id.slice(-6)}</CardTitle>
                      <Badge className="bg-orange-100 text-orange-800">En camino</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Map */}
                    <div className="h-40 rounded-xl overflow-hidden">
                      <MapContainer
                        center={mapCenter}
                        zoom={14}
                        style={{ height: "100%", width: "100%" }}
                        zoomControl={false}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={mapCenter}>
                          <Popup>Destino: {order.address?.street || "Sin dirección"}</Popup>
                        </Marker>
                      </MapContainer>
                    </div>

                    {order.address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">{order.address.street}</p>
                          <p className="text-sm text-muted-foreground">
                            {order.address.postal_code} {order.address.city}
                          </p>
                        </div>
                      </div>
                    )}

                    {order.notes && (
                      <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 border border-yellow-200">
                        <strong>Nota:</strong> {order.notes}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div>
                        <p className="font-bold text-lg">{order.total.toFixed(2)} €</p>
                        <p className="text-sm font-bold text-orange-600">
                          {order.payment_method === "card" ? "🔴 TPV / TARJETA" : "💵 COBRAR EFECTIVO"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {order.address && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openGoogleMaps(order.address)}
                            data-testid={`navigate-active-${order.order_id}`}
                          >
                            <Navigation className="w-4 h-4 mr-1" />
                            Navegar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => handleDeliver(order.order_id)}
                          data-testid={`deliver-${order.order_id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Entregar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DriverLayout>
  );
}
